import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { FiSave, FiCreditCard, FiAlertCircle, FiMail } from "react-icons/fi";
import toast from "react-hot-toast";

const EMPTY_FORM = {
    // Stripe
    stripeEnabled: false,
    stripePublicKey: "",
    stripeSecretKey: "",
    stripeWebhookSecret: "",
    // PayPal
    paypalEnabled: false,
    paypalClientId: "",
    paypalMode: "sandbox", // sandbox | live
    // General
    currency: "USD",
    testMode: true,
    // Email
    emailEnabled: false,
    emailServiceId: "",
    emailTemplateId: "",
    emailPublicKey: "",
    // Resend Settings
    emailProvider: "emailjs", // emailjs | resend
    resendApiKey: "",
    resendFromEmail: "onboarding@resend.dev", 
    // Admin Notifications
    adminEmailEnabled: false,
    adminEmail: "",
    adminTemplateId: "",
    // ATH Móvil
    athEnabled: false,
    athPhoneNumber: "",
    athInstructions: "Send the total via Transfer to a Person. Include your Order ID in the message.",
};

export default function AdminPayments() {
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        async function load() {
            try {
                // Load both public and private configs
                const [publicSnap, privateSnap] = await Promise.all([
                    getDoc(doc(db, "siteConfig", "payments")),
                    getDoc(doc(db, "siteConfig", "payments_private"))
                ]);
                
                let combinedData = { ...EMPTY_FORM };
                if (publicSnap.exists()) combinedData = { ...combinedData, ...publicSnap.data() };
                if (privateSnap.exists()) combinedData = { ...combinedData, ...privateSnap.data() };
                
                setForm(combinedData);
            } catch (err) { 
                console.error("Load error:", err);
                toast.error("Could not load payment settings."); 
            }
            setLoading(false);
        }
        load();
    }, []);

    function handleChange(e) {
        const { name, value, type, checked } = e.target;
        setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    }

    async function save() {
        setSaving(true);
        try {
            // Split data back into public and private
            const publicData = {
                stripeEnabled: form.stripeEnabled,
                stripePublicKey: form.stripePublicKey,
                paypalEnabled: form.paypalEnabled,
                paypalClientId: form.paypalClientId,
                paypalMode: form.paypalMode,
                currency: form.currency,
                testMode: form.testMode,
                emailEnabled: form.emailEnabled,
                emailProvider: form.emailProvider,
                // Move these to public so frontend can send emails
                emailServiceId: form.emailServiceId,
                emailTemplateId: form.emailTemplateId,
                emailPublicKey: form.emailPublicKey,
                adminEmailEnabled: form.adminEmailEnabled,
                adminEmail: form.adminEmail,
                adminTemplateId: form.adminTemplateId,
                athEnabled: form.athEnabled,
                athPhoneNumber: form.athPhoneNumber,
                athInstructions: form.athInstructions,
            };

            const privateData = {
                resendApiKey: form.resendApiKey,
                resendFromEmail: form.resendFromEmail,
                stripeSecretKey: form.stripeSecretKey,
                stripeWebhookSecret: form.stripeWebhookSecret,
            };

            await Promise.all([
                setDoc(doc(db, "siteConfig", "payments"), publicData),
                setDoc(doc(db, "siteConfig", "payments_private"), privateData)
            ]);
            
            toast.success("Payment settings saved securely!");
        } catch (err) { 
            console.error("Save error:", err);
            toast.error("Failed to save."); 
        }
        setSaving(false);
    }

    if (loading) return <div className="admin-loading">Loading…</div>;

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 24 }}>
                <button className="admin-btn-primary" onClick={save} disabled={saving}>
                    <FiSave size={16} /> {saving ? "Saving…" : "Save Changes"}
                </button>
            </div>

            {form.testMode && (
                <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 10, padding: "12px 20px", marginBottom: 24, display: "flex", gap: 12, alignItems: "center", color: "#856404", fontSize: 14 }}>
                    <FiAlertCircle size={18} />
                    <span><strong>Test Mode is ON.</strong> No real payments will be processed. Switch to Live Mode when ready for production.</span>
                </div>
            )}

            {/* General */}
            <div className="admin-card" style={{ padding: 28, marginBottom: 24 }}>
                <p style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", color: "#888", letterSpacing: 0.5, marginBottom: 20 }}>General Settings</p>
                <div className="admin-form-grid">
                    <div className="admin-form-field">
                        <label>Currency</label>
                        <select name="currency" value={form.currency} onChange={handleChange}>
                            <option value="USD">USD – US Dollar</option>
                            <option value="EUR">EUR – Euro</option>
                            <option value="GBP">GBP – British Pound</option>
                            <option value="CAD">CAD – Canadian Dollar</option>
                        </select>
                    </div>
                    <div className="admin-form-field" style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                        <input type="checkbox" id="testMode" name="testMode" checked={form.testMode} onChange={handleChange} />
                        <label htmlFor="testMode" style={{ cursor: "pointer" }}>Enable Test Mode (use sandbox credentials)</label>
                    </div>
                </div>
            </div>

            {/* Stripe */}
            <div className="admin-card" style={{ padding: 28, marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                    <div style={{ width: 40, height: 40, background: "#635bff", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FiCreditCard size={20} color="white" />
                    </div>
                    <div>
                        <p style={{ fontWeight: 700, fontSize: 16, margin: 0 }}>Stripe</p>
                        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>Accept credit & debit cards</p>
                    </div>
                    <div style={{ marginLeft: "auto" }}>
                        <label className="toggle-switch">
                            <input type="checkbox" name="stripeEnabled" checked={form.stripeEnabled} onChange={handleChange} />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>
                </div>
                {form.stripeEnabled && (
                    <div className="admin-form-grid">
                        <div className="admin-form-field full">
                            <label>Stripe Publishable Key {form.testMode ? "(Test)" : "(Live)"}</label>
                            <input
                                name="stripePublicKey"
                                value={form.stripePublicKey}
                                onChange={handleChange}
                                placeholder={form.testMode ? "pk_test_..." : "pk_live_..."}
                            />
                            <span style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                                Find your key at <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" style={{ color: "var(--accent)" }}>dashboard.stripe.com/apikeys</a>
                            </span>
                        </div>
                        <div className="admin-form-field full">
                            <label>Stripe Secret Key {form.testMode ? "(Test)" : "(Live)"}</label>
                            <input
                                name="stripeSecretKey"
                                type="password"
                                value={form.stripeSecretKey}
                                onChange={handleChange}
                                placeholder={form.testMode ? "sk_test_..." : "sk_live_..."}
                            />
                            <p style={{ fontSize: 11, color: "#e74c3c", marginTop: 4, fontWeight: 600 }}>
                                🔒 This key is stored securely and never shared with customers.
                            </p>
                        </div>
                        <div className="admin-form-field full">
                            <div style={{ background: "#f0f7ff", padding: 16, borderRadius: 10, border: "1.5px solid #d0e7ff" }}>
                                <p style={{ fontSize: 13, fontWeight: 700, color: "#0056b3", margin: "0 0 8px" }}>Final Step: Webhook Configuration</p>
                                <p style={{ fontSize: 12, color: "#004085", margin: 0, lineHeight: 1.6 }}>
                                    To automatically process orders, add this Webhook URL to your <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer" style={{ fontWeight: 700, textDecoration: "underline" }}>Stripe Dashboard</a>:<br />
                                    <code>https://us-central1-lbbookspr-768e3.cloudfunctions.net/stripeWebhook</code> <br />
                                    <em>(Select event: <strong>checkout.session.completed</strong>)</em>
                                </p>
                                <div style={{ marginTop: 12 }}>
                                    <label style={{ fontSize: 11, fontWeight: 700, color: "#0056b3", display: "block", marginBottom: 4 }}>Webhook Secret (whsec_...)</label>
                                    <input 
                                        name="stripeWebhookSecret"
                                        type="password"
                                        value={form.stripeWebhookSecret}
                                        onChange={handleChange}
                                        placeholder="whsec_..."
                                        style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #d0e7ff", fontSize: 13 }}
                                    />
                                    <p style={{ fontSize: 10, color: "#888", marginTop: 4 }}>Recommended for secure payment verification.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* PayPal */}
            <div className="admin-card" style={{ padding: 28 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                    <div style={{ width: 40, height: 40, background: "#003087", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span style={{ color: "white", fontWeight: 900, fontSize: 18, fontStyle: "italic" }}>P</span>
                    </div>
                    <div>
                        <p style={{ fontWeight: 700, fontSize: 16, margin: 0 }}>PayPal</p>
                        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>Accept PayPal & credit cards via PayPal</p>
                    </div>
                    <div style={{ marginLeft: "auto" }}>
                        <label className="toggle-switch">
                            <input type="checkbox" name="paypalEnabled" checked={form.paypalEnabled} onChange={handleChange} />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>
                </div>
                {form.paypalEnabled && (
                    <div className="admin-form-grid">
                        <div className="admin-form-field full">
                            <label>PayPal Client ID {form.testMode ? "(Sandbox)" : "(Live)"}</label>
                            <input
                                name="paypalClientId"
                                value={form.paypalClientId}
                                onChange={handleChange}
                                placeholder="AaBb..."
                            />
                            <span style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                                Find your Client ID at <a href="https://developer.paypal.com/dashboard/" target="_blank" rel="noreferrer" style={{ color: "var(--accent)" }}>developer.paypal.com</a>
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* ATH Móvil */}
            <div className="admin-card" style={{ padding: 28, marginTop: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                    <div style={{ width: 40, height: 40, background: "#ff6600", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FiCreditCard size={20} color="white" />
                    </div>
                    <div>
                        <p style={{ fontWeight: 700, fontSize: 16, margin: 0 }}>ATH Móvil (Manual)</p>
                        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>Manual verification via phone transfer</p>
                    </div>
                    <div style={{ marginLeft: "auto" }}>
                        <label className="toggle-switch">
                            <input type="checkbox" name="athEnabled" checked={form.athEnabled} onChange={handleChange} />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>
                </div>
                {form.athEnabled && (
                    <div className="admin-form-grid">
                        <div className="admin-form-field">
                            <label>ATH Móvil Phone Number</label>
                            <input
                                name="athPhoneNumber"
                                value={form.athPhoneNumber}
                                onChange={handleChange}
                                placeholder="(787) 000-0000"
                            />
                        </div>
                        <div className="admin-form-field full">
                            <label>Custom Instructions</label>
                            <textarea
                                name="athInstructions"
                                value={form.athInstructions}
                                onChange={handleChange}
                                style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid #ddd", minHeight: 80, fontSize: 14 }}
                                placeholder="Explain how to pay..."
                            />
                        </div>
                        <div className="admin-form-field full">
                            <div style={{ background: "#fff5eb", padding: 16, borderRadius: 10, border: "1.5px solid #ffe8cc" }}>
                                <p style={{ fontSize: 13, fontWeight: 700, color: "#d9480f", margin: "0 0 8px" }}>How it works:</p>
                                <p style={{ fontSize: 12, color: "#854d0e", margin: 0, lineHeight: 1.6 }}>
                                    1. Customers see these instructions and your phone number at checkout.<br />
                                    2. They send the payment manually and include the Order ID in the ATH Móvil message.<br />
                                    3. You manually approve the order in the <strong>Orders</strong> section after verifying your app.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Email Settings */}
            <div className="admin-card" style={{ padding: 28, marginTop: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                    <div style={{ width: 40, height: 40, background: "#ff6b6b", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FiMail size={20} color="white" />
                    </div>
                    <div>
                        <p style={{ fontWeight: 700, fontSize: 16, margin: 0 }}>Order Confirmation Emails</p>
                        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>Send automated purchase receipts</p>
                    </div>
                    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 15 }}>
                        <select 
                            name="emailProvider" 
                            value={form.emailProvider} 
                            onChange={handleChange}
                            style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13, fontWeight: 600 }}
                        >
                            <option value="emailjs">EmailJS</option>
                            <option value="resend">Resend (3,000 Free)</option>
                        </select>
                        <label className="toggle-switch">
                            <input type="checkbox" name="emailEnabled" checked={form.emailEnabled} onChange={handleChange} />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>
                </div>
                {form.emailEnabled && (
                    <div className="admin-form-grid">
                        {form.emailProvider === "emailjs" ? (
                            <>
                                <div className="admin-form-field">
                                    <label>EmailJS Service ID</label>
                                    <input name="emailServiceId" value={form.emailServiceId} onChange={handleChange} placeholder="service_..." />
                                </div>
                                <div className="admin-form-field">
                                    <label>EmailJS Template ID</label>
                                    <input name="emailTemplateId" value={form.emailTemplateId} onChange={handleChange} placeholder="template_..." />
                                </div>
                                <div className="admin-form-field full">
                                    <label>EmailJS Public Key</label>
                                    <input name="emailPublicKey" value={form.emailPublicKey} onChange={handleChange} placeholder="A1B2C3..." />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="admin-form-field full">
                                    <label>Resend API Key</label>
                                    <input name="resendApiKey" type="password" value={form.resendApiKey} onChange={handleChange} placeholder="re_123456789..." />
                                </div>
                                <div className="admin-form-field">
                                    <label>From Email (Verified Domain)</label>
                                    <input name="resendFromEmail" value={form.resendFromEmail} onChange={handleChange} placeholder="shop@yourdomain.com" />
                                    <p style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Default: <code>onboarding@resend.dev</code> (only sends to you)</p>
                                </div>
                            </>
                        )}
                        
                        <div className="admin-form-field full">
                            <div style={{ background: "#f8f9fa", padding: 16, borderRadius: 10, border: "1.5px solid #eee" }}>
                                <p style={{ fontSize: 13, fontWeight: 700, margin: "0 0 8px" }}>Instructions:</p>
                                <p style={{ fontSize: 12, color: "#666", margin: 0, lineHeight: 1.6 }}>
                                    <strong>Resend:</strong> You must verify your domain in Resend to send to customers. <br />
                                    The email will be sent as a professional HTML receipt.
                                </p>
                            </div>
                        </div>

                        {/* Admin Alerts Sub-section */}
                        <div className="admin-form-field full" style={{ marginTop: 20 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                                <input 
                                    type="checkbox" 
                                    id="adminEmailEnabled" 
                                    name="adminEmailEnabled" 
                                    checked={form.adminEmailEnabled} 
                                    onChange={handleChange} 
                                />
                                <label htmlFor="adminEmailEnabled" style={{ fontWeight: 600, fontSize: 14 }}>Send separate alert to Admin (You)</label>
                            </div>
                            
                            {form.adminEmailEnabled && (
                                <div className="admin-form-grid" style={{ padding: 20, background: "#fdfdfd", border: "1px solid #eee", borderRadius: 12 }}>
                                    <div className="admin-form-field">
                                        <label>Admin Recipient Email</label>
                                        <input 
                                            name="adminEmail" 
                                            value={form.adminEmail} 
                                            onChange={handleChange} 
                                            placeholder="your@email.com" 
                                        />
                                    </div>
                                    {form.emailProvider === 'emailjs' && (
                                        <div className="admin-form-field">
                                            <label>EmailJS Admin Template ID</label>
                                            <input 
                                                name="adminTemplateId" 
                                                value={form.adminTemplateId} 
                                                onChange={handleChange} 
                                                placeholder="template_..." 
                                            />
                                        </div>
                                    )}
                                    <p style={{ fontSize: 11, color: "#888", margin: "10px 0 0" }}>
                                        {form.emailProvider === 'resend' 
                                            ? "For Resend, the admin will be BCC'd on the customer receipt. No separate template needed."
                                            : "Tip: Create a separate template in EmailJS for yourself (e.g. 'New Order Received!'). It can use the same variables as the customer receipt."}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                .toggle-switch { position: relative; display: inline-block; width: 48px; height: 26px; }
                .toggle-switch input { opacity: 0; width: 0; height: 0; }
                .toggle-slider { position: absolute; cursor: pointer; inset: 0; background: #ccc; border-radius: 26px; transition: .4s; }
                .toggle-slider:before { position: absolute; content: ""; height: 20px; width: 20px; left: 3px; bottom: 3px; background: white; border-radius: 50%; transition: .4s; }
                .toggle-switch input:checked + .toggle-slider { background: var(--green-primary); }
                .toggle-switch input:checked + .toggle-slider:before { transform: translateX(22px); }
            `}</style>
        </div>
    );
}
